import { cn } from "@/lib/utils";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";

/**
 * The Hiive bot's face: the Hiive mark. Legacy personal-bot surfaces may
 * still pass an owner, shown as a small corner portrait.
 */
export function AssistantAvatar({
  ownerAvatarUrl,
  ownerName,
  className,
}: {
  ownerAvatarUrl?: string | null;
  ownerName?: string;
  className?: string;
}) {
  const hasOwner = !!ownerAvatarUrl || !!ownerName;
  return (
    <span
      className={cn("relative inline-flex size-10 shrink-0", className)}
      aria-label={
        hasOwner && ownerName ? `Hiive bot, asked by ${ownerName}` : "Hiive bot"
      }
      role="img"
    >
      <span className="flex size-full items-center justify-center rounded-[30%] border border-amber-500/40 bg-zinc-950">
        <HiiveLogoIcon width="75%" height="75%" aria-hidden="true" />
      </span>
      {hasOwner ? (
        <span className="absolute -right-1 -bottom-1 flex size-[45%] items-center justify-center overflow-hidden rounded-full border-2 border-background bg-muted text-[9px] font-semibold">
          {ownerAvatarUrl ? (
            // biome-ignore lint/performance/noImgElement: Small shared bot-owner badge uses the actual uploaded owner portrait.
            <img
              src={ownerAvatarUrl}
              alt=""
              className="size-full object-cover"
            />
          ) : (
            ownerName?.trim().charAt(0).toUpperCase() || "·"
          )}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The small Hiive mark after the bot's name, in place of a "· bot" suffix:
 * the name already says Hiive bot, and the mark is what tells it from a
 * person who happened to pick that display name.
 */
export function HiiveBotMark({ className }: { className?: string }) {
  return (
    <span
      role="img"
      aria-label="Bot"
      title="Hiive bot"
      className={cn(
        "inline-flex size-3.5 shrink-0 items-center justify-center rounded-[30%] bg-zinc-950 ring-1 ring-amber-500/40",
        className,
      )}
    >
      <HiiveLogoIcon width="72%" height="72%" aria-hidden="true" />
    </span>
  );
}
